import React, { useEffect, useState } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { useMsal } from '@azure/msal-react';
import { acquireApiAccessTokenAsync } from '../../services/auth/AuthService';

const useStyles = makeStyles({
    shimmer: {
        display: 'inline-block',
        minWidth: '120px',
        minHeight: '80px',
        borderRadius: '4px',
        background: 'linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%)',
        backgroundSize: '200% 100%',
        animationName: {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
        },
        animationDuration: '1.4s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'linear',
    },
});

// Module-level cache — blob URLs persist for the session lifetime
const imageCache = new Map<string, string>();

interface AuthenticatedImageProps {
    src: string;
    alt?: string;
    className?: string;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ src, alt, className }) => {
    const styles = useStyles();
    const { instance } = useMsal();
    const [blobUrl, setBlobUrl] = useState<string | null>(() => {
        if (!src || src.startsWith('data:')) return src || null;
        return imageCache.get(src) ?? null;
    });
    const [isLoading, setIsLoading] = useState(!blobUrl);

    useEffect(() => {
        if (!src) return;

        if (src.startsWith('data:')) {
            setBlobUrl(src);
            setIsLoading(false);
            return;
        }

        const cached = imageCache.get(src);
        if (cached) {
            setBlobUrl(cached);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        let cancelled = false;

        const fetchImage = async (): Promise<void> => {
            try {
                const token = await acquireApiAccessTokenAsync({ msalInstance: instance });
                const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
                const apimKey = process.env.REACT_APP_NEXUS_APIM_SUBSCRIPTION_KEY;
                const apimHeader = process.env.REACT_APP_NEXUS_APIM_KEY_HEADER_NAME;
                if (apimKey && apimHeader) headers[apimHeader] = apimKey;
                const response = await fetch(src, { headers });
                if (cancelled || !response.ok) return;
                const blob = await response.blob();
                if (cancelled) return;
                const objectUrl = URL.createObjectURL(blob);
                imageCache.set(src, objectUrl);
                setBlobUrl(objectUrl);
            } catch {
                // fails silently — broken image is preferable to an error
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void fetchImage();
        return () => { cancelled = true; };
    }, [src, instance]);

    if (isLoading) {
        return <span className={styles.shimmer} aria-label="Loading image" role="img" />;
    }

    if (!blobUrl) return null;

    return <img src={blobUrl} alt={alt ?? 'image'} className={className} loading="lazy" />;
};

export default AuthenticatedImage;
